const _ = require("lodash");
const colors = require("../../util/ColorUtil").getDiagramColors();
const ShapeArea = require("./ShapeArea");
const {V} = require("./VectorDraw");

// The aspect ratio of the given plot height/width will be maintained and the plot will be centered as necessary
// ctx: context to draw upon
// x, y: top left coordinates
// w, h: width and height

module.exports = function(
	ctx,
	x,
	y,
	grid,
	leftSum,
	rightSum,
	leftCarry,
	rightCarry,
	rows,
	cols,
	tilt
) {
	// By convention, rows are lower left to upper right (/)
	// Columns are lower right to upper left (\)
	// For development, I recommend hardcoding tilt = "R" to edge-align the grid
	const getValidOptArg = (arg, rowOrCol) => {
		if (rowOrCol === "row") {
			return arg && _.isArray(arg) && arg.length === grid.length ? arg : [];
		} else {
			return arg && _.isArray(arg) && arg.length === grid[0].length ? arg : [];
		}
	};
	leftCarry = getValidOptArg(leftCarry, "col");
	rightCarry = getValidOptArg(rightCarry, "row");
	rows = getValidOptArg(rows, "row");
	cols = getValidOptArg(cols, "col");
	tilt = tilt && typeof tilt === "string" && tilt.toUpperCase()[0];
	if (tilt !== "L" && tilt !== "R") tilt = false;
	let rot;
	if (tilt === "L") rot = Math.PI / -4;
	if (tilt === "R") rot = Math.PI / 4;
	const unitSize = 35;
	const numRows = grid.length;
	const numCols = grid[0].length;
	const tableSize = Math.max(numRows, numCols);
	const gridSize = tableSize * 2 - 1; // Constant pulled out from if/else blocks below; used to layout grid
	let width, height, xOffset, yOffset;
	const rowEnds = [[], []];
	const colEnds = [[], []];
	if (tableSize > 2) {
		width = 8.2;
		height = 8;
		xOffset = 2.2;
		yOffset = 0.8;
		if (tilt === "R") {
			xOffset = 2.0;
			yOffset = 1.4;
		}
		if (tilt === "L") {
			xOffset = 1.9;
			yOffset = 1.5;
		}
	} else {
		width = 5.5;
		height = 5.5;
		xOffset = 1.4;
		yOffset = 1;
		if (tilt === "R") {
			xOffset = 1.3;
			yOffset = 1.2;
		}
		if (tilt === "L") {
			xOffset = 1.4;
			yOffset = 1.2;
		}
	}

	const canvasWidth = width * unitSize;
	const canvasHeight = height * unitSize;

	ctx.setSize(canvasWidth, canvasHeight);

	// Set up rotation if tilting
	if (tilt) {
		ctx.save();
		ctx.translate(canvasWidth / 2, canvasHeight / 2);
		if (rot) ctx.rotate(rot);
		ctx.translate(canvasWidth / -2, canvasHeight / -2);
	}

	// Create shape area
	const A = ShapeArea(
		ctx,
		xOffset * unitSize,
		yOffset * unitSize,
		gridSize * unitSize,
		gridSize * unitSize,
		gridSize,
		gridSize
	);

	// Drawing the gray rectangle; expressions found by trial and error
	const rectangle = [
		V(tableSize - 0.5 + numCols - numRows, numCols + numRows - 0.5),
		V(tableSize - 0.5 + numCols, numCols - 0.5),
		V(tableSize - 0.5, -0.5),
		V(tableSize - 0.5 - numRows, numRows - 0.5),
	];
	A.shape(rectangle, {
		stroke: colors.mediumlightgray,
		fill: colors.mediumlightgray,
	});

	// Blank coordinates, expressed in units
	const p = [];
	for (let i = 0; i < numRows; i++) {
		p[i] = [];
		for (let j = 0; j < numCols; j++) {
			p[i][j] = V(gridSize / 2 + j - i, 0.5 + i + j);
		}
	}

	// Row and column end coordinates, expressed in units
	for (let j = 0; j < numRows; j++) {
		rowEnds[0][j] = p[j][0].add(V(-0.8, -0.8));
		rowEnds[1][j] = p[j][numCols - 1].add(V(0.1, 0.1));
	}
	for (let j = 0; j < numCols; j++) {
		colEnds[0][j] = p[0][j].add(V(0.8, -0.8));
		colEnds[1][j] = p[numRows - 1][j].add(V(-0.1, 0.1));
	}

	// Draw the row and column highlights
	const lineOpts = {
		lineWidth: 40,
		stroke: colors.lightblue,
		lineCap: "round",
	};
	for (let i = 0; i < numRows; i++) {
		if (rows[i] === 1) {
			A.path([rowEnds[0][i], rowEnds[1][i]], lineOpts);
		}
		if (cols[i] === 1) {
			A.path([colEnds[0][i], colEnds[1][i]], lineOpts);
		}
	}

	// Draw the lines along the sum sides
	A.line(
		rectangle[2],
		rectangle[3].add(V(-(tableSize - 1) / 2, (tableSize - 1) / 2)),
		{lineWidth: 2}
	);
	A.line(
		rectangle[1],
		rectangle[2].add(V(-(tableSize - 1) / 2, -(tableSize - 1) / 2)),
		{lineWidth: 2}
	);

	// Draw the circles and blanks
	for (let i = 0; i < numRows; i++) {
		for (let j = 0; j < numCols; j++) {
			A.circle(p[i][j], 0.5, {stroke: colors.white, fill: colors.white});
			if (grid[i][j] === 0) {
				if (tilt) {
					A.label(p[i][j].add(V(0, -0.05)), "$\\smaller{\\fakeblank{1}}$", {
						dir: [0, 0],
					});
				} else {
					A.label(p[i][j], "$\\blank{1}$", {dir: [0, 0]});
				}
			} else if (grid[i][j] < 0) {
				A.label(
					p[i][j].add(V(0, -0.05)),
					"$\\smaller{\\fakeblank[" + grid[i][j] * -1 + "]{1}}$",
					{dir: [0, 0]}
				);
			} else {
				A.label(p[i][j].add(V(0, -0.05)), "\\smaller{" + grid[i][j] + "}", {
					dir: [0, 0],
				});
			}
		}
	}

	// Labels
	const hasLongRightSum = rightSum.length === numCols + 1 ? 1 : 0;
	const hasLongLeftSum = leftSum.length === numRows + 1 ? 1 : 0;
	const hasRightSumComma = rightSum.length === 4 ? 1 : 0;
	const hasLeftSumComma = leftSum.length === 4 ? 1 : 0;
	const topLeft = [];
	const topRight = [];
	const bottomLeft = [];
	const bottomRight = [];

	for (let i = 0; i < numRows; i++) {
		// Draw left sum positions
		bottomLeft[i] = [
			V.rot(
				p[numRows - 1 - i][0].add(V(-1, 0)),
				-Math.PI / 4,
				p[numRows - 1 - i][0]
			).add(V(-1.2, -1.2)),
			V.rot(
				p[numRows - 1 - i][0].add(V(1, 0)),
				-Math.PI / 4,
				p[numRows - 1 - i][0]
			).add(V(-1.2, -1.2)),
		];
		A.label(
			bottomLeft[i][0],
			bottomLeft[i][1],
			"\\smaller{" + leftSum[i + hasLongLeftSum] + "}"
		);

		// Draw right carry positions
		topRight[i] = [
			V.rot(
				p[numRows - 1 - i][numCols - 1].add(V(-1, 0)),
				-Math.PI / 4,
				p[numRows - 1 - i][numCols - 1]
			).add(V(0.5, 0.5)),
			V.rot(
				p[numRows - 1 - i][numCols - 1].add(V(1, 0)),
				-Math.PI / 4,
				p[numRows - 1 - i][numCols - 1]
			).add(V(0.5, 0.5)),
		];
		if (rightCarry[i]) {
			A.label(
				topRight[i][0],
				topRight[i][1],
				"\\smaller{" + rightCarry[i] + "}"
			);
		}
	}

	for (let i = 0; i < numCols; i++) {
		// Draw right sum positions
		bottomRight[i] = [
			V.rot(p[0][i].add(V(-1, 0)), Math.PI / 4, p[0][i]).add(V(1.2, -1.2)),
			V.rot(p[0][i].add(V(1, 0)), Math.PI / 4, p[0][i]).add(V(1.2, -1.2)),
		];
		A.label(
			bottomRight[i][0],
			bottomRight[i][1],
			"\\smaller{" + rightSum[i + hasLongRightSum] + "}"
		);

		// Draw left carry positions
		topLeft[i] = [
			V.rot(
				p[numRows - 1][i].add(V(-1, 0)),
				Math.PI / 4,
				p[numRows - 1][i]
			).add(V(-0.5, 0.5)),
			V.rot(p[numRows - 1][i].add(V(1, 0)), Math.PI / 4, p[numRows - 1][i]).add(
				V(-0.5, 0.5)
			),
		];
		if (leftCarry[i]) {
			A.label(topLeft[i][0], topLeft[i][1], "\\smaller{" + leftCarry[i] + "}");
		}
	}

	// Add higher place value digits and commas if four digits
	if (hasLongRightSum) {
		A.label(
			bottomRight[0][0].add(V(-1, -1)),
			bottomRight[0][1].add(V(-1, -1)),
			"\\smaller{" + rightSum[0] + "}"
		);
	}

	if (hasRightSumComma) {
		A.label(
			bottomRight[0][0].add(V(-0.5, -0.5)),
			bottomRight[0][1].add(V(-0.5, -0.5)),
			","
		);
	}

	if (hasLongLeftSum) {
		A.label(
			bottomLeft[0][0].add(V(-1, 1)),
			bottomLeft[0][1].add(V(-1, 1)),
			"\\smaller{" + leftSum[0] + "}"
		);
	}

	if (hasLeftSumComma) {
		A.label(
			bottomLeft[0][0].add(V(-0.5, 0.5)),
			bottomLeft[0][1].add(V(-0.5, 0.5)),
			","
		);
	}

	// Add equal signs
	// TODO: In a 2x2 grid, three digit sums will overlap the equals signs.
	// The solution will require modifying the width / height constants.
	// Postponing for now because a 3-digit sum isn't possible in the Cross Sums engine
	// using only digits from 1 to 4.
	if (tableSize > 2) {
		const shift = 1.4;
		A.label(
			bottomRight[0][0].add(V(-shift, -shift)),
			bottomRight[0][1].add(V(-shift, -shift)),
			"="
		);
		A.label(
			bottomLeft[0][0].add(V(-shift, shift)),
			bottomLeft[0][1].add(V(-shift, shift)),
			"="
		);
	} else {
		const shift = 0.65;
		A.label(
			bottomRight[0][0].add(V(-shift, -shift)),
			bottomRight[0][1].add(V(-shift, -shift)),
			"="
		);
		A.label(
			bottomLeft[0][0].add(V(-shift, shift)),
			bottomLeft[0][1].add(V(-shift, shift)),
			"="
		);
	}

	// Restore canvas setting if tilted
	if (tilt) ctx.restore();
};
